'use client';

import type { ReactNode } from 'react';
import React from 'react';
import { Button, HeaderLogoSVG } from '@ui';
import { Container } from '@layout';

type FooterLink = { title: string; url: string; target: string };

type FooterLinkSection = { section_title: string; links: FooterLink[] };

type LayoutLocale = 'en' | 'es';

type FooterProps = {
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  locale?: LayoutLocale;
  /** Brand row next to the mark (`site_name` in locale JSON). */
  siteNameLabel?: string;
  /** Copyright line after the year (`site_owner`). */
  siteOwnerLabel?: string;
  copyrightLabel?: string;
  /** Plain-text blurb; ignored when `footerBlurbNode` is set. */
  footerBlurb?: string;
  /** Replace the default `<p className="sub">` blurb entirely (rich content). */
  footerBlurbNode?: ReactNode;
  /** Optional block after the blurb paragraph or `footerBlurbNode`. */
  footerBlurbExtra?: ReactNode;
  /** Optional slot under the brand row (e.g. app-specific notes). */
  brandExtra?: ReactNode;
  /** When set, rendered in `.brand` before the label instead of the default `HeaderLogoSVG`. */
  brandIcon?: ReactNode;
  linkSections?: FooterLinkSection[];
  resolveInternalHref?: (href: string, locale: LayoutLocale) => string;
  /** e.g. admin-only controls for a matching `section_title` */
  renderLinkSectionExtra?: (section: FooterLinkSection) => ReactNode | null;
};

const DEFAULT_LINK_SECTIONS: FooterLinkSection[] = [
  {
    section_title: 'Explore',
    links: [
      { title: 'Home', url: '/', target: '_self' },
      { title: 'About', url: '/about', target: '_self' },
      { title: 'Portfolio', url: '/portfolio', target: '_self' },
    ],
  },
];

const defaultResolveInternalHref = (href: string): string => href;

type MailtoParsed = { addr: string; search: string };

function parseMailtoHref(href: string): MailtoParsed | null {
  if (!href.startsWith('mailto:')) return null;
  const raw = href.slice('mailto:'.length);
  const q = raw.indexOf('?');
  const addr = (q >= 0 ? raw.slice(0, q) : raw).trim();
  const search = q >= 0 ? raw.slice(q) : '';
  if (!addr) return null;
  return { addr, search };
}

/** Base64 UTF-8 for obfuscation — ASCII `btoa(addr)` misses non-Latin locals. */
function encodeMailtoAddr(addr: string): string {
  const bytes = new TextEncoder().encode(addr);
  let bin = '';
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin);
}

function decodeMailtoAddr(b64: string): string {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

const ObfuscatedMailtoControl: React.FC<{ mailtoHref: string; title: string }> = ({
  mailtoHref,
  title,
}) => {
  const parsed = parseMailtoHref(mailtoHref);
  if (!parsed) {
    return null;
  }
  const encoded = encodeMailtoAddr(parsed.addr);
  const { search } = parsed;

  const open = () => {
    try {
      const addr = decodeMailtoAddr(encoded);
      window.location.assign(`mailto:${addr}${search}`);
    } catch {
      /* ignore */
    }
  };

  return (
    <span
      role='link'
      tabIndex={0}
      className='contact'
      onClick={open}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      {title}
    </span>
  );
};

const Footer: React.FC<FooterProps> = ({
  classes = '',
  containerId,
  containerClasses,
  locale = 'en',
  siteNameLabel,
  siteOwnerLabel,
  copyrightLabel,
  footerBlurb,
  footerBlurbNode,
  footerBlurbExtra,
  brandExtra,
  brandIcon,
  linkSections,
  resolveInternalHref = defaultResolveInternalHref,
  renderLinkSectionExtra,
}) => {
  const sections = linkSections ?? DEFAULT_LINK_SECTIONS;
  const defaultName = 'Tulio Solorzano';
  const brandLabel = siteNameLabel ?? siteOwnerLabel ?? defaultName;
  const copyrightOwner = siteOwnerLabel ?? defaultName;
  const copyright =
    copyrightLabel ?? (locale === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.');
  const blurb =
    footerBlurb ??
    (locale === 'es'
      ? 'Diseñando y desarrollando experiencias digitales accesibles.'
      : 'Designing and building accessible digital experiences.');

  return (
    <footer id='footer' className={classes || undefined}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        {/* Desktop 3-col: .footer-col + two `footer_links` → class `link-sections-3` */}
        <div className={`footer-grid${sections.length === 2 ? ' link-sections-3' : ''}`}>
          <div className='footer-col'>
            <div className='brand'>
              {brandIcon ?? <HeaderLogoSVG logoClass='footer-logo' />}
              {brandLabel}
            </div>
            {brandExtra != null && brandExtra !== false ? (
              <div className='footer-brand-extra'>{brandExtra}</div>
            ) : null}
            {footerBlurbNode != null ? (
              <div className='footer-blurb-node'>{footerBlurbNode}</div>
            ) : (
              <p className='sub'>{blurb}</p>
            )}
            {footerBlurbExtra != null && footerBlurbExtra !== false ? (
              <div className='footer-blurb-extra'>{footerBlurbExtra}</div>
            ) : null}
          </div>
          {sections.map((section, idx) => {
            const trailing = renderLinkSectionExtra?.(section) ?? null;
            return (
              <div className='foot-col' key={`${section.section_title}-${idx}`}>
                <h2>{section.section_title}</h2>
                {Array.isArray(section.links) &&
                  section.links.map((link: FooterLink, linkIdx: number) => {
                    // Obfuscated mailto — preserves ?subject= & other query on the mailto URL
                    if (link.url.startsWith('mailto:')) {
                      return (
                        <ObfuscatedMailtoControl
                          key={linkIdx}
                          mailtoHref={link.url}
                          title={link.title}
                        />
                      );
                    }
                    let href = link.url;
                    try {
                      href = resolveInternalHref(href, locale);
                    } catch {
                      /* keep href */
                    }
                    return (
                      <Button
                        key={linkIdx}
                        href={href}
                        target={link.target}
                        as='a'
                        className='btn-link'
                        rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                        label={link.title}
                      />
                    );
                  })}
                {trailing}
              </div>
            );
          })}
        </div>
      </Container>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <div className='sep'></div>
        <small className='footer-copyright'>
          © <span>{new Date().getFullYear()}</span> {copyrightOwner}. {copyright}
        </small>
      </Container>
    </footer>
  );
};

export type { FooterLinkSection, FooterProps };
export default Footer;
